import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AutoComplete, Col, Form, Input, Row, Select, theme } from "antd";
import { observer } from "mobx-react";
import { EEcommerceSystemName } from "@devowl-wp/api-real-commerce";
import { CUSTOMER_TYPE_FIELD } from "./CustomerTypeSwitch.js";
import { usePriorityCountries } from "../../../../hooks/usePriorityCountries.js";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../utils/matomoMask.js";
import { useAddressAutocomplete } from "../../hooks/useAddressAutocomplete.js";
const WANTS_INVOICE_ADDRESS_FIELD = "wantsInvoiceAddress";
const AddressFields = observer(({ form })=>{
    const { checkoutV2Store } = useStores();
    const { product: { data: { productPlan: { product: { ecommerceSystem: { name: ecommerceSystemName } } }, paddlePayLink } } } = checkoutV2Store;
    const { token } = theme.useToken();
    const countries = usePriorityCountries(ecommerceSystemName === EEcommerceSystemName.Paddle ? "paddle" : "iso-3166-1-alpha-2");
    const overwriteCheckoutCountry = paddlePayLink?.overwriteCheckoutCountry;
    const customerType = Form.useWatch(CUSTOMER_TYPE_FIELD);
    const wantsInvoiceAddress = Form.useWatch(WANTS_INVOICE_ADDRESS_FIELD);
    const isInvoiceAddressRendered = customerType === "company" || !!wantsInvoiceAddress;
    const autocomplete = useAddressAutocomplete(form);
    const countrySelect = /*#__PURE__*/ _jsx(Form.Item, {
        name: "country",
        label: __("Country"),
        required: true,
        rules: [
            {
                required: true,
                message: __("Please choose your country.")
            }
        ],
        children: /*#__PURE__*/ _jsxs(Select, {
            transitionName: null,
            disabled: !!overwriteCheckoutCountry,
            placeholder: __("Choose your country..."),
            showSearch: true,
            notFoundContent: __("No data"),
            optionFilterProp: "children",
            ...MATOMO_MASK,
            children: [
                /*#__PURE__*/ _jsx(Select.OptGroup, {
                    label: __("EU"),
                    children: countries.eu.map(({ key, label })=>/*#__PURE__*/ _jsx(Select.Option, {
                            value: key,
                            children: label
                        }, key))
                }),
                /*#__PURE__*/ _jsx(Select.OptGroup, {
                    label: __("Rest of the world"),
                    children: countries.restOfTheWorld.map(({ key, label })=>/*#__PURE__*/ _jsx(Select.Option, {
                            value: key,
                            children: label
                        }, key))
                })
            ]
        })
    });
    // When no invoice address is given, the ZIP is only collected for tax
    // calculation — and buyers find that confusing ("why do you want my ZIP?").
    // Attaching a small muted suffix to the label explains it inline with zero
    // extra vertical space. The suffix is omitted once the user opts into an
    // invoice address, since then the ZIP is part of the billing address and
    // the explanation would be misleading.
    const zipLabel = isInvoiceAddressRendered ? __("Postal code") : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            __("Postal code"),
            /*#__PURE__*/ _jsxs("span", {
                style: {
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    marginLeft: 6,
                    fontWeight: 400
                },
                children: [
                    "\xb7 ",
                    __("for sales tax needed")
                ]
            })
        ]
    });
    const zipItem = /*#__PURE__*/ _jsx(Form.Item, {
        name: "zip",
        label: zipLabel,
        required: true,
        rules: [
            {
                required: true,
                message: __("Please enter your postcode (ZIP code).")
            }
        ],
        children: /*#__PURE__*/ _jsx(Input, {
            autoComplete: "postal-code",
            inputMode: "numeric",
            ...MATOMO_MASK
        })
    });
    if (!isInvoiceAddressRendered) {
        // Compact layout: Zip + Country in one row. The tax-hint now lives as
        // a muted suffix inside the Postal code label above, so no standalone
        // explanation row is needed here.
        return /*#__PURE__*/ _jsxs(Row, {
            gutter: 16,
            children: [
                /*#__PURE__*/ _jsx(Col, {
                    xs: 24,
                    sm: 10,
                    children: zipItem
                }),
                /*#__PURE__*/ _jsx(Col, {
                    xs: 24,
                    sm: 14,
                    children: countrySelect
                })
            ]
        });
    }
    // Street field — Google Places autocomplete wraps the `<Input>` when a
    // key is configured and the buyer has previously granted Maps consent
    // via Real Cookie Banner (the hook silently loads the library on mount
    // in that case). Without a configured key or without prior consent the
    // field stays a plain `<Input>`; suggestions simply don't appear and the
    // buyer enters the address manually.
    const streetField = autocomplete.apiKeyConfigured ? /*#__PURE__*/ _jsx(Form.Item, {
        name: "street",
        label: __("Street and number"),
        required: true,
        rules: [
            {
                required: true,
                message: __("Please enter your street.")
            }
        ],
        children: /*#__PURE__*/ _jsx(AutoComplete, {
            className: "checkout-v2-address-autocomplete",
            options: autocomplete.enabled ? autocomplete.suggestions : [],
            onSearch: autocomplete.onSearch,
            onSelect: (value, option)=>autocomplete.onSelect(value, option),
            children: /*#__PURE__*/ _jsx(Input, {
                autoComplete: "street-address",
                ...MATOMO_MASK
            })
        })
    }) : /*#__PURE__*/ _jsx(Form.Item, {
        name: "street",
        label: __("Street and number"),
        required: true,
        rules: [
            {
                required: true,
                message: __("Please enter your street.")
            }
        ],
        children: /*#__PURE__*/ _jsx(Input, {
            autoComplete: "street-address",
            ...MATOMO_MASK
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            streetField,
            /*#__PURE__*/ _jsxs(Row, {
                gutter: 16,
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        xs: 24,
                        sm: 10,
                        children: zipItem
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        xs: 24,
                        sm: 14,
                        children: /*#__PURE__*/ _jsx(Form.Item, {
                            name: "city",
                            label: __("City / Town"),
                            required: true,
                            rules: [
                                {
                                    required: true,
                                    message: __("Please enter your city/town.")
                                }
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                autoComplete: "address-level2",
                                ...MATOMO_MASK
                            })
                        })
                    })
                ]
            }),
            countrySelect
        ]
    });
});
export { AddressFields, WANTS_INVOICE_ADDRESS_FIELD };
